import * as THREE from 'three';
import { LevelConfig, LevelStats } from '../types';
import { Zombie, BossAbilityEffect } from './Zombie';
import { ZOMBIES, BOSSES } from '../data/zombieData';
import { LEVEL } from '../constants';
import { EventEmitter } from '../core/EventEmitter';

export class Level {
  config: LevelConfig;
  zombies: Zombie[] = [];
  totalKills = 0;
  earnedCoins = 0;
  startTime = 0;
  isComplete = false;
  events: EventEmitter;
  mapColliders: THREE.Box3[] = [];
  private scene: THREE.Scene | null = null;
  pendingPlayerDamage = 0;
  // Spitter projectiles
  private projectiles: { mesh: THREE.Group; velocity: THREE.Vector3; damage: number; life: number }[] = [];

  constructor(config: LevelConfig, events: EventEmitter) {
    this.config = config;
    this.events = events;
  }

  start(scene: THREE.Scene, mapColliders: THREE.Box3[]): void {
    this.startTime = performance.now();
    this.mapColliders = mapColliders;
    this.scene = scene;
    this.spawnZombies(scene);
  }

  private spawnZombies(scene: THREE.Scene): void {
    // Boss level: only spawn boss, no regular zombies
    if (this.config.hasBoss) {
      const bossConfig = BOSSES.find(b => b.stage === this.config.stage);
      if (bossConfig) {
        const bossPos = new THREE.Vector3(0, 0, -12);
        const boss = new Zombie(
          bossConfig,
          bossPos,
          this.config.hpMultiplier,
          this.config.damageMultiplier,
          this.config.speedMultiplier,
          true,
          bossConfig.visual
        );
        this.zombies.push(boss);
        scene.add(boss.mesh);
      }
      return;
    }

    const availableTypes = this.config.zombieTypes;
    const count = this.config.zombieCount;

    for (let i = 0; i < count; i++) {
      const typeId = availableTypes[Math.floor(Math.random() * availableTypes.length)];
      const zombieConfig = ZOMBIES[typeId];
      if (!zombieConfig) continue;

      // Random spawn position away from player (player starts at 0,0,0)
      const angle = Math.random() * Math.PI * 2;
      const dist = 8 + Math.random() * 15;
      const pos = new THREE.Vector3(
        Math.cos(angle) * dist,
        0,
        Math.sin(angle) * dist
      );

      // Clamp to map bounds
      pos.x = THREE.MathUtils.clamp(pos.x, -13, 13);
      pos.z = THREE.MathUtils.clamp(pos.z, -13, 13);

      const zombie = new Zombie(
        zombieConfig,
        pos,
        this.config.hpMultiplier,
        this.config.damageMultiplier,
        this.config.speedMultiplier
      );

      this.zombies.push(zombie);
      scene.add(zombie.mesh);
    }

  }

  update(dt: number, playerPosition: THREE.Vector3, camera?: THREE.Camera): void {
    if (this.isComplete) return;
    this.pendingPlayerDamage = 0;

    for (const zombie of this.zombies) {
      zombie.update(dt, playerPosition, this.mapColliders, camera);

      // Process boss ability effects
      if (zombie.isBoss && zombie.pendingAbilityEffects.length > 0) {
        this.processBossAbilities(zombie, playerPosition);
      }
    }
  }

  private processBossAbilities(boss: Zombie, playerPos: THREE.Vector3): void {
    const effects = boss.pendingAbilityEffects.splice(0);
    for (const fx of effects) {
      switch (fx.type) {
        case 'damage': {
          // Direct damage to player (if within boss attack range * 2)
          const dx = boss.position.x - playerPos.x;
          const dz = boss.position.z - playerPos.z;
          const dist = Math.sqrt(dx * dx + dz * dz);
          if (dist < boss.config.attackRange * 2.5) {
            this.pendingPlayerDamage += fx.damage ?? 0;
          }
          break;
        }
        case 'areaDamage': {
          // Damage if player is within radius
          if (fx.position) {
            const dx = fx.position.x - playerPos.x;
            const dz = fx.position.z - playerPos.z;
            const dist = Math.sqrt(dx * dx + dz * dz);
            if (dist < (fx.radius ?? 5)) {
              // Falloff damage based on distance
              const falloff = 1 - (dist / (fx.radius ?? 5));
              this.pendingPlayerDamage += Math.round((fx.damage ?? 0) * falloff);
            }
          }
          break;
        }
        case 'spawn': {
          // Spawn normal zombies near the boss (max 5 summoned alive at once)
          if (this.scene && fx.position) {
            const summonedAlive = this.zombies.filter(z => !z.isDead() && !z.isBoss).length;
            const canSpawn = Math.max(0, 5 - summonedAlive);
            const count = Math.min(fx.spawnCount ?? 1, 3, canSpawn);
            const types = Object.keys(ZOMBIES);
            for (let i = 0; i < count; i++) {
              const typeId = types[Math.floor(Math.random() * types.length)];
              const cfg = ZOMBIES[typeId];
              if (!cfg) continue;
              const angle = Math.random() * Math.PI * 2;
              const dist = 2 + Math.random() * 3;
              const pos = new THREE.Vector3(
                fx.position.x + Math.cos(angle) * dist,
                0,
                fx.position.z + Math.sin(angle) * dist,
              );
              pos.x = THREE.MathUtils.clamp(pos.x, -13, 13);
              pos.z = THREE.MathUtils.clamp(pos.z, -13, 13);
              const z = new Zombie(cfg, pos, this.config.hpMultiplier, this.config.damageMultiplier, this.config.speedMultiplier);
              this.zombies.push(z);
              this.scene.add(z.mesh);
            }
          }
          break;
        }
        case 'buff': {
          // Speed buff nearby zombies
          if (fx.position) {
            const r = fx.radius ?? 10;
            for (const z of this.zombies) {
              if (z.isDead() || z === boss) continue;
              const dx = z.position.x - fx.position.x;
              const dz = z.position.z - fx.position.z;
              if (Math.sqrt(dx * dx + dz * dz) < r) {
                z.config.speed *= (fx.speedMultiplier ?? 1);
              }
            }
          }
          break;
        }
      }
    }
  }

  onZombieKilled(zombie: Zombie): void {
    this.totalKills++;
    this.earnedCoins += zombie.config.reward;

    // Boss killed: all summoned minions die too
    if (zombie.isBoss) {
      for (const z of this.zombies) {
        if (!z.isDead() && !z.isBoss) {
          z.takeDamage(99999);
        }
      }
    }

    // Check level complete
    const alive = this.zombies.filter(z => !z.isDead());
    if (alive.length === 0) {
      this.isComplete = true;
      const bonus = LEVEL.CLEAR_BONUS[this.config.level - 1] ?? 50;
      this.earnedCoins += bonus;

      const stats: LevelStats = {
        kills: this.totalKills,
        coins: this.earnedCoins,
        time: (performance.now() - this.startTime) / 1000,
      };
      // Pass last zombie position for key drop
      const lastPos = zombie.position.clone();
      this.events.emit('level:clear', {
        stage: this.config.stage,
        level: this.config.level,
        stats,
        lastZombiePos: lastPos,
      });
    }
  }

  getAttackingZombies(playerPos: THREE.Vector3): { zombie: Zombie; damage: number }[] {
    const attacks: { zombie: Zombie; damage: number }[] = [];
    for (const zombie of this.zombies) {
      if (!zombie.isDead() && zombie.canAttack()) {
        const dx = zombie.position.x - playerPos.x;
        const dz = zombie.position.z - playerPos.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < zombie.config.attackRange) {
          if (zombie.config.id === 'spitter') {
            // Spitter: fire visible projectile instead of instant damage
            const dmg = zombie.performAttack();
            this.spawnSpitterProjectile(zombie.position, playerPos, dmg);
          } else {
            const dmg = zombie.performAttack();
            attacks.push({ zombie, damage: dmg });
          }
        }
      }
    }
    return attacks;
  }

  private spawnSpitterProjectile(from: THREE.Vector3, target: THREE.Vector3, damage: number): void {
    if (!this.scene) return;

    const group = new THREE.Group();
    group.position.set(from.x, from.y + 1.2, from.z);

    // Acid ball (green glowing sphere)
    const ballGeo = new THREE.SphereGeometry(0.08, 6, 4);
    const ballMat = new THREE.MeshBasicMaterial({ color: 0x66ff00 });
    group.add(new THREE.Mesh(ballGeo, ballMat));

    // Dripping trail
    const trailGeo = new THREE.ConeGeometry(0.04, 0.2, 4);
    trailGeo.rotateX(Math.PI / 2);
    const trailMat = new THREE.MeshBasicMaterial({
      color: 0x44cc00, transparent: true, opacity: 0.6,
    });
    const trail = new THREE.Mesh(trailGeo, trailMat);
    trail.position.z = 0.12;
    group.add(trail);

    // Glow light (skip on mobile for performance)
    const isMob = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isMob) {
      const light = new THREE.PointLight(0x66ff00, 1, 3);
      group.add(light);
    }

    // Direction toward player
    const dir = new THREE.Vector3(
      target.x - from.x,
      (target.y + 1.0) - (from.y + 1.2),
      target.z - from.z
    ).normalize();

    const lookTarget = group.position.clone().add(dir);
    group.lookAt(lookTarget);

    this.scene.add(group);

    const speed = 12;
    this.projectiles.push({
      mesh: group,
      velocity: dir.multiplyScalar(speed),
      damage,
      life: 0,
    });
  }

  updateProjectiles(dt: number, playerPos: THREE.Vector3): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.life += dt;
      p.mesh.position.addScaledVector(p.velocity, dt);

      // Check hit player (within 0.8m)
      const dx = p.mesh.position.x - playerPos.x;
      const dy = p.mesh.position.y - (playerPos.y - 0.5);
      const dz = p.mesh.position.z - playerPos.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist < 0.8) {
        // Hit player
        this.pendingPlayerDamage += p.damage;
        this.removeProjectile(i);
      } else if (p.life > 3 || p.mesh.position.y < -1) {
        // Missed / expired
        this.removeProjectile(i);
      }
    }
  }

  private removeProjectile(index: number): void {
    const p = this.projectiles[index];
    if (this.scene) this.scene.remove(p.mesh);
    p.mesh.traverse(c => {
      if (c instanceof THREE.Mesh) { c.geometry.dispose(); (c.material as THREE.Material).dispose(); }
      if (c instanceof THREE.PointLight) c.dispose();
    });
    this.projectiles.splice(index, 1);
  }

  cleanup(scene: THREE.Scene): void {
    // Clean up projectiles
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      this.removeProjectile(i);
    }
    for (const zombie of this.zombies) {
      scene.remove(zombie.mesh);
      zombie.dispose();
    }
    this.zombies = [];
  }

  getAliveCount(): number {
    return this.zombies.filter(z => !z.isDead()).length;
  }

  getTotalCount(): number {
    return this.zombies.length;
  }
}
